import React, { useContext, useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHttp, useNotification } from 'src/hooks';
import { AuthContext } from 'src/contexts';
import LoadingSpinner from 'src/components/common/ui/loader/LoadingSpinner';

interface Props {
  AdAccountId: string;
  setIsStepValid: (isValid: boolean) => void;
}

interface Pixel {
  id: string;
  name: string;
}

const CreatePixel: React.FC<Props> = ({ AdAccountId, setIsStepValid }) => {
  const notify = useNotification();
  const { sendRequest, isLoading } = useHttp();
  const { accessToken } = useContext(AuthContext);
  const [pixels, setPixels] = useState<Pixel[]>([]);
  const [selectedPixel, setSelectedPixel] = useState<string | null>(null);

  const validationSchema = Yup.object().shape({
    pixelName: Yup.string().required('Pixel name is required'),
  });

  useEffect(() => {
    setIsStepValid(pixels.length > 0);
  }, [pixels, setIsStepValid]);

  useEffect(() => {
    if (!AdAccountId) return;

    sendRequest(
      {
        url: `pixel/${AdAccountId}`,
        accessToken,
      },
      data => {
        setPixels(data?.data || []);
      },
    );
  }, [AdAccountId, accessToken]);

  const handleCreatePixel = async (values: { pixelName: string }, { resetForm }: any) => {
    sendRequest(
      {
        url: `pixel`,
        accessToken,
        method: 'POST',
        payload: {
          name: values.pixelName,
          adAccountId: AdAccountId,
        },
      },
      data => {
        if (data) {
          notify('Pixel Created successfully!', 'success');
          resetForm(); // Reset form after success
        }
      },
    );
  };
  return (
    <div className="my-8 p-6 border border-gray-300 rounded-2xl max-w-2xl mx-auto">
      <h2 className="text-[28px] text-center font-semibold font-inter text-black mb-8">
        Manage Pixels
      </h2>
      {isLoading ? (
        <LoadingSpinner />
      ) : pixels.length > 0 ? (
        <ul className="py-56 flex flex-wrap justify-center gap-4">
          {pixels.map(pixel => (
            <li
              key={pixel.id}
              className={`border p-4 rounded-lg cursor-pointer transition-all 
                ${
                  selectedPixel === pixel.id
                    ? 'border-blue-500 bg-blue-100'
                    : 'border-gray-300 hover:bg-gray-100'
                }`}
              onClick={() => setSelectedPixel(pixel.id)}
            >
              {pixel.name}
            </li>
          ))}
        </ul>
      ) : (
        <Formik
          initialValues={{ pixelName: '' }}
          validationSchema={validationSchema}
          onSubmit={handleCreatePixel}
        >
          {() => (
            <Form className="space-y-4">
              <div>
                <label htmlFor="pixelName" className="block text-gray-700">
                  Pixel Name
                </label>
                <Field
                  type="text"
                  id="pixelName"
                  name="pixelName"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
                <ErrorMessage name="pixelName" component="div" className="text-red-500" />
              </div>
              <button
                type="submit"
                className="w-full p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create Pixel
              </button>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default CreatePixel;
